import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, Theme } from '@mui/material/styles';
import i18n from '../i18n/i18n';
import { themeService, ThemeConfig } from '../services/themeService';
import { minimalistTheme } from '../utils/minimalistTheme';

interface ThemeContextType {
  muiTheme: Theme;
  theme: ThemeConfig | null;
  loading: boolean;
  error: string | null;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

// Recompute MUI theme.direction whenever i18n flips so drawers, popovers,
// margins, and paddings all flip with the page.
const dirAwareTheme = (base: Theme): Theme => {
  const dir = (i18n.language || 'en').toLowerCase().startsWith('ar') ? 'rtl' : 'ltr';
  // createTheme(base, overrides) extends; spreading loses computed values.
  return createTheme(base, { direction: dir });
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [muiTheme, setMuiTheme] = useState<Theme>(dirAwareTheme(minimalistTheme));

  useEffect(() => {
    const onLang = () => setMuiTheme(dirAwareTheme(minimalistTheme));
    i18n.on('languageChanged', onLang);
    return () => { i18n.off('languageChanged', onLang); };
  }, []);

  const loadTheme = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load theme configuration
      const themeConfig = await themeService.getThemeConfig();
      if (themeConfig) {
        setTheme(themeConfig);

        // Apply theme CSS variables to DOM (colors, fonts)
        themeService.applyTheme(themeConfig);
      }

      // Always use the dark minimalistTheme for MUI components
      // Firestore theme only controls CSS variables, not MUI component styling
      setMuiTheme(dirAwareTheme(minimalistTheme));

    } catch (err) {
      console.error('Failed to load theme:', err);
      setError('Failed to load theme configuration');
      // Fallback to default theme
      setMuiTheme(minimalistTheme);
    } finally {
      setLoading(false);
    }
  };

  const refreshTheme = async () => {
    themeService.clearCache();
    await loadTheme();
  };

  useEffect(() => {
    loadTheme();
  }, []);

  const value: ThemeContextType = {
    muiTheme,
    theme,
    loading,
    error,
    refreshTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={muiTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
